import styled from "@emotion/styled";
import {
  Header,
  Menu,
  MenuSection,
  MenuItem,
  Popover,
  Button as AriaButton
} from "react-aria-components";
import { motion } from "framer-motion";

// Sit above every app overlay layer, including PMP's WindowManager floating
// windows (z-index ~90002+) and their modals. The action sheet is a top-level
// modal surface, so it must win over any windowed content that triggered it.
/** Backdrop sits below the popover but above all page content. */
const Z_INDEX_BACKDROP = 999998;
/** Popover floats above the backdrop. */
const Z_INDEX_POPOVER = 999999;

export const Backdrop = styled(motion.div)`
  label: DropdownActionSheetBackdrop;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEX_BACKDROP};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

export const InvisibleTrigger = styled(AriaButton)`
  label: DropdownActionSheetTrigger;
  position: fixed;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
`;

export const StyledPopover = styled(Popover)`
  label: DropdownActionSheetPopover;
  background:
    radial-gradient(
      circle at top left,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0.1)
    ),
    linear-gradient(
      to right,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0.17)
    );
  backdrop-filter: blur(12px);
  border-radius: 1.6rem;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.12),
    0 8px 32px rgba(0, 0, 0, 0.08),
    inset 9px 9px 9px rgba(0, 0, 0, 0.015),
    inset 1px 1px 1px rgba(255, 255, 255, 0.9),
    inset -1px -1px 1px rgba(255, 255, 255, 0.6),
    inset -9px -9px 9px rgba(255, 255, 255, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: ${Z_INDEX_POPOVER};
  touch-action: manipulation;
  -webkit-overflow-scrolling: touch;

  &[data-entering] {
    animation: popoverScaleIn 220ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  &[data-exiting] {
    animation: popoverScaleOut 150ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  @keyframes popoverScaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes popoverScaleOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.9);
    }
  }

  &[data-theme="admin"] {
    background: #fff;
    backdrop-filter: none;
    border-radius: 12px;
    box-shadow:
      0 4px 24px rgba(0, 0, 0, 0.12),
      0 1px 4px rgba(0, 0, 0, 0.08);
  }
`;

export const PopoverContent = styled.div`
  label: DropdownActionSheetContent;
  min-width: 220px;
  max-width: 300px;
  max-height: calc(
    100vh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)
  );
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (pointer: coarse) {
    max-width: calc(
      100vw - 2rem - env(safe-area-inset-left) - env(safe-area-inset-right)
    );
  }
`;

export const MenuWrapper = styled.div`
  label: DropdownActionSheetMenuWrapper;
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
`;

export const FloatingHighlight = styled.div`
  label: DropdownActionSheetHighlight;
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  top: 0;
  height: 0;
  opacity: 0;
  border-radius: 1.6rem;
  background: rgba(0, 0, 0, 0.06);
  pointer-events: none;
  z-index: 0;
  transform: scale(1);
  transition: transform 200ms ease-out;

  @media (pointer: fine) {
    &[data-pressed="true"] {
      transform: scale(0.97);
    }
  }

  [data-theme="admin"] & {
    border-radius: 8px;
  }
`;

export const MenuSectionGroup = styled(MenuSection)`
  label: DropdownActionSheetSection;
  display: block;
`;

export const SectionHeader = styled(Header)`
  label: DropdownActionSheetSectionHeader;
  padding: 0.5rem 1rem 0.25rem 1.2rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.4);
  user-select: none;

  [data-theme="admin"] & {
    padding: 0.45rem 0.9rem 0.2rem 1rem;
  }
`;

export const StyledMenu = styled(Menu)`
  label: DropdownActionSheetMenu;
  position: relative;
  outline: none;
  padding: 0.8rem 0;
  z-index: 1;

  [data-theme="admin"] & {
    padding: 0.4rem 0;
  }
`;

export const MenuHeader = styled.div`
  label: DropdownActionSheetHeader;
  padding: 0.9rem 1.6rem 0.8rem 1rem;
  text-align: left;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  [data-theme="admin"] & {
    padding: 0.7rem 1rem 0.6rem 1rem;
  }
`;

export const MenuTitle = styled.div`
  label: DropdownActionSheetTitle;
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  letter-spacing: -0.01em;
  line-height: 1.3;

  [data-theme="admin"] & {
    font-size: 0.95rem;
    font-weight: 600;
  }
`;

export const MenuMessage = styled.div`
  label: DropdownActionSheetMessage;
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.2;
  margin-top: 0.2rem;
  /* Preserve author newlines so a multi-line message renders as stacked lines
     (e.g. a metadata block); whitespace runs still collapse. */
  white-space: pre-line;
`;

export const IconWrapper = styled.span`
  label: DropdownActionSheetIcon;
  display: flex;
  align-items: center;
  justify-content: center;
  --icon-size-default: 24px;
  width: var(--icon-size, var(--icon-size-default));
  height: var(--icon-size, var(--icon-size-default));
  flex-shrink: 0;
  color: inherit;

  svg {
    display: block;
    width: calc(var(--icon-size, var(--icon-size-default)) * 0.9);
    height: calc(var(--icon-size, var(--icon-size-default)) * 0.9);
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  label: DropdownActionSheetItem;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem 0.65rem 1.2rem;
  cursor: pointer;
  outline: none;
  border: none;
  background: transparent;
  font-size: 1.1rem;
  font-weight: 400;
  text-align: left;
  width: 100%;
  letter-spacing: -0.02em;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  box-sizing: border-box;

  &[data-divider="true"] {
    border-top: 0.5px solid rgba(0, 0, 0, 0.1);
    margin-top: 0.4rem;
    padding-top: 1rem;
  }

  &[data-style="default"] {
    color: rgba(0, 0, 0, 0.85);
  }

  &[data-style="destructive"] {
    color: #ff3b30;
  }

  &[data-style="cancel"] {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 600;
  }

  &[data-is-selected="true"] {
    font-weight: 600;
  }

  /* Muted but still interactive — distinct from data-disabled (not-allowed). */
  &[data-dimmed="true"] {
    opacity: 0.45;
  }

  &[data-disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (pointer: coarse) {
    padding: 0.8rem 1rem;
  }

  strong,
  b {
    font-weight: 500;
  }

  [data-theme="admin"] & {
    font-size: 0.875rem;
    padding: 0.4rem 0.9rem 0.4rem 1rem;
    gap: 0.5rem;
    letter-spacing: -0.01em;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.88);
  }

  [data-theme="admin"] &[data-is-selected="true"] {
    color: #1668dc;
    font-weight: 600;
  }

  /* The admin base color rule above ties in specificity with
     &[data-style="destructive"] and would otherwise win on source order,
     silently graying out destructive rows under the admin theme. */
  [data-theme="admin"] &[data-style="destructive"] {
    color: var(--color-error, #ff3b30);
  }

  /* Submenu items have no FloatingHighlight; use CSS hover/focus background. */
  [data-submenu="true"] &:hover,
  [data-submenu="true"] &[data-focused],
  [data-submenu="true"] &[data-focus-visible] {
    background: rgba(0, 0, 0, 0.06);
  }

  [data-theme="admin"] [data-submenu="true"] &:hover,
  [data-theme="admin"] [data-submenu="true"] &[data-focused],
  [data-theme="admin"] [data-submenu="true"] &[data-focus-visible] {
    background: rgba(0, 0, 0, 0.05);
  }
`;

export const ItemTitle = styled.span`
  flex: 1;
  padding: 0 0.6rem 0 0;
  transform: translateY(0.065em);

  [data-icon-position="right"] > & {
    flex: 1;
  }
`;

/**
 * Two-line text column used when a menu item has a `description`. Holds the
 * title line and the muted description stacked vertically; takes the same
 * flex slot the bare `ItemTitle` would occupy.
 */
export const ItemTextColumn = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0 0.6rem 0 0;
`;

export const ItemTitleLine = styled.span`
  transform: translateY(0.065em);
`;

export const ItemDescription = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: 0;
  color: rgba(0, 0, 0, 0.45);
  white-space: normal;
`;

export const ShortcutLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.35);
  letter-spacing: 0.02em;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 1rem;
  transform: translateY(0.065em);
`;

/**
 * Right-aligned chevron glyph shown on submenu trigger rows.
 * Mirrors ShortcutLabel metrics. aria-hidden should be set on the element.
 */
export const SubmenuChevron = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.35);
  padding-left: 0.75rem;
  transform: translateY(0.02em);
`;

export const LoadingContainer = styled.div`
  label: DropdownActionSheetLoading;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 2rem;
  min-width: 80px;
  min-height: 140px;
`;

export const LoadingSpinner = styled.div`
  label: DropdownActionSheetSpinner;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
